import { Controller } from 'stimulus';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';

export default class ModalWindowController extends Controller {
  static targets = ['window', 'scrollable', 'opener'];

  openedNumber = 0;

  openedStack = [];

  minZIndex = 3;

  connect() {
    window.addEventListener('CustomModalWindowOpened', this.onCustomModalOpened);
    window.addEventListener('CustomModalWindowClosed', this.onCustomModalClosed);
    window.addEventListener('turbolinks:render', clearAllBodyScrollLocks);
  }

  disconnect() {
    window.removeEventListener('CustomModalWindowOpened', this.onCustomModalOpened);
    window.removeEventListener('CustomModalWindowClosed', this.onCustomModalClosed);
    window.removeEventListener('turbolinks:render', clearAllBodyScrollLocks);
  }

  closeLastWindow() {
    const modal = this.openedStack[this.openedStack.length - 1];
    if (modal) {
      this.closeWindow(modal);
    }
  }

  findScrollable(modal) {
    return modal.querySelector('[data-modal-window-target="scrollable"]');
  }

  pushToStack(modal) {
    this.openedNumber += 1;
    this.openedStack.push(modal);
  }

  popFromStack(modal) {
    this.openedNumber -= 1;
    const index = this.openedStack.indexOf(modal);
    return this.openedStack.splice(index, 1)[0];
  }

  closeWindow(modal) {
    if (
      modal.dataset.modalWindowTarget !== 'window'
      || this.openedStack.indexOf(modal) < 0
    ) {
      return;
    }

    const childModals = modal.querySelectorAll('[data-modal-window-target="window"]');
    for (let i = 0; i < childModals.length; i++) {
      this.closeWindow(childModals[i]);
    }

    const style = window.getComputedStyle(modal);
    const rawDuration = style.transitionDuration;
    const duration = rawDuration.indexOf('ms') > -1
      ? parseFloat(rawDuration) : parseFloat(rawDuration) * 1000;

    modal.classList.remove('active');

    for (let i = 0; i < this.openerTargets.length; i++) {
      if (this.openerTargets[i].dataset.modalId === modal.id) {
        this.openerTargets[i].classList.remove('active');
      }
    }

    this.popFromStack(modal);
    if (this.openedNumber === 0) {
      window.setTimeout(() => enableBodyScroll(this.findScrollable(modal)), duration);
    }

    window.dispatchEvent(new CustomEvent('ModalWindowClosed', { detail: modal }));
  }

  onOpen(event) {
    const button = event.target;
    const modal = document.getElementById(button.dataset.modalId);
    if (
      this.openedStack.indexOf(modal) >= 0
      || window.getComputedStyle(modal).display === 'none'
    ) {
      // The modal window doesn't exist or has already been opened — exiting:
      return;
    }

    if (modal) {
      modal.classList.add('active');
      button.classList.add('active');
    }

    this.scrollableTargets.forEach((scrollable) => scrollable.scrollTo(0, 0));

    if (this.openedNumber === 0) {
      disableBodyScroll(this.findScrollable(modal), { reserveScrollBarGap: true });
      document.body.style.right = 0;
    }

    this.pushToStack(modal);
    modal.style.zIndex = this.minZIndex + this.openedNumber;
    window.dispatchEvent(new CustomEvent('ModalWindowOpened', { detail: modal }));
  }

  onToggle(event) {
    const button = event.target;
    if (button.classList.contains('active')) {
      this.closeWindow(document.getElementById(button.dataset.modalId));
    } else {
      this.onOpen(event);
    }
  }

  findNearestModal(currentElement) {
    let element = currentElement;
    while (element) {
      if (element.dataset.modalWindowTarget === 'window') {
        return element;
      }

      element = element.parentElement;
    }

    return null;
  }

  onClose(event) {
    this.closeWindow(this.findNearestModal(event.target.parentElement));
  }

  onKeypress(event) {
    if (event.code === 'Escape') {
      this.closeLastWindow();
    }
  }

  onCustomModalOpened = (event) => {
    if (this.openedNumber === 0 && event.detail) {
      disableBodyScroll(event.detail, { reserveScrollBarGap: true });
    }
    this.pushToStack(event.detail);
  };

  onCustomModalClosed = (event) => {
    this.popFromStack(event.detail);
    if (this.openedNumber === 0 && event.detail) {
      enableBodyScroll(event.detail);
    }
  };
}
